import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { GetStartedProvider } from "@/components/GetStartedDialog";
import { AuthProvider } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Portfolio from "@/pages/Portfolio";
import PortfolioDetail from "@/pages/PortfolioDetail";
import Services from "@/pages/Services";
import ServicePillar from "@/pages/ServicePillar";
import About from "@/pages/About";
import LocationPage from "@/pages/LocationPage";
import IndustryPage from "@/pages/IndustryPage";
import { LocationsHub, IndustriesHub } from "@/pages/LocalHubs";
import SiteAudit from "@/pages/SiteAudit";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminJournalList from "@/pages/admin/AdminJournalList";
import AdminJournalEditor from "@/pages/admin/AdminJournalEditor";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminLeads from "@/pages/admin/AdminLeads";
import RequireAdmin from "@/pages/admin/RequireAdmin";
import NewsletterConfirm from "@/pages/NewsletterConfirm";
import NewsletterUnsubscribe from "@/pages/NewsletterUnsubscribe";
import Confirmation from "@/pages/Confirmation";
import NotFound from "@/pages/NotFound";
import GATracker from "@/components/GATracker";
import { Toaster } from "sonner";

const TOASTER_OPTIONS = {
  style: {
    fontFamily: '"Inter", ui-sans-serif, sans-serif',
    border: "1px solid hsl(var(--line))",
    borderRadius: "4px",
  },
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <GetStartedProvider>
            <GATracker />
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServicePillar />} />
                <Route path="/work" element={<Portfolio />} />
                <Route path="/work/:id" element={<PortfolioDetail />} />
                <Route path="/portfolio" element={<Navigate to="/work" replace />} />
                <Route path="/portfolio/:id" element={<PortfolioLegacyRedirect />} />
                <Route path="/locations" element={<LocationsHub />} />
                <Route path="/web-design/:slug" element={<LocationPage />} />
                <Route path="/industries" element={<IndustriesHub />} />
                <Route path="/industries/:slug" element={<IndustryPage />} />
                <Route path="/about" element={<About />} />
                <Route path="/audit" element={<SiteAudit />} />
                <Route path="/blog" element={<Journal />} />
                <Route path="/blog/:slug" element={<JournalPost />} />
                {/* Backward-compat: /journal → /blog for SEO / old inbound links */}
                <Route path="/journal" element={<Navigate to="/blog" replace />} />
                <Route path="/journal/:slug" element={<JournalLegacyRedirect />} />

                {/* Admin */}
                <Route path="/admin" element={<Navigate to="/admin/journal" replace />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin/journal"
                  element={<RequireAdmin><AdminJournalList /></RequireAdmin>}
                />
                <Route
                  path="/admin/journal/new"
                  element={<RequireAdmin><AdminJournalEditor /></RequireAdmin>}
                />
                <Route
                  path="/admin/journal/edit/:id"
                  element={<RequireAdmin><AdminJournalEditor /></RequireAdmin>}
                />
                <Route
                  path="/admin/subscribers"
                  element={<RequireAdmin><AdminSubscribers /></RequireAdmin>}
                />
                <Route
                  path="/admin/leads"
                  element={<RequireAdmin><AdminLeads /></RequireAdmin>}
                />

                {/* Newsletter (public) */}
                <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
                <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />

                {/* Form submission confirmation (Google Ads conversion URL) */}
                <Route path="/confirmation" element={<Confirmation />} />

                <Route path="/contact" element={<Navigate to="/" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
            <Toaster
              position="bottom-right"
              toastOptions={TOASTER_OPTIONS}
            />
          </GetStartedProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function PortfolioLegacyRedirect() {
  const path = window.location.pathname.replace(/^\/portfolio/, "/work");
  return <Navigate to={path} replace />;
}

function JournalLegacyRedirect() {
  const path = window.location.pathname.replace(/^\/journal/, "/blog");
  return <Navigate to={path} replace />;
}

export default App;
